import { useMoney } from '@lounge-fe/ui-kit/src/hooks/useMoney'
import type { PartialDeep } from 'type-fest'
import type { MoneyProps } from './types'
import { useShopify } from '@/hooks/useShopify'
import { MoneyV2 } from '@shopify/hydrogen-react/storefront-api-types'

/**
 * Renders a locale-aware money string from a Storefront API `MoneyV2` object.
 *
 * This component is a thin fork of Shopify Hydrogen's `Money` component that has
 * been adapted to use the `locale` supplied by our Theme-level `ShopifyProvider`
 * (via the `useShopify()` hook).
 *
 * @typeParam ComponentGeneric - The element or React component to render. Defaults to `'div'`.
 *
 * @param props.data - A valid `MoneyV2` object containing `amount` and `currencyCode`.
 * @param [props.as] - HTML tag or React component to render instead of a `div`.
 * @param [props.withoutCurrency] - Hide the currency symbol in the output.
 * @param [props.withoutTrailingZeros] - Trim insignificant trailing zeros from the amount.
 * @param [props.measurement] - Optional per-unit measurement to append (for example `{ referenceUnit: 'kg' }`).
 * @param [props.measurementSeparator='/'] - String inserted between the amount and the measurement.
 *
 * @returns A React element displaying the formatted money string.
 */
export function Money<ComponentGeneric extends React.ElementType = 'div'>({
  data,
  as,
  withoutCurrency,
  withoutTrailingZeros,
  measurement,
  measurementSeparator = '/',
  ...passthroughProps
}: MoneyProps<ComponentGeneric>): JSX.Element {
  if (!isMoney(data)) {
    throw new Error(
      `<Money/> needs a valid 'data' prop that has 'amount' and 'currencyCode'`,
    )
  }

  const { locale } = useShopify()

  const moneyObject = useMoney(data, locale)
  const Component = as ?? 'div'

  let output = moneyObject.localizedString

  if (withoutCurrency || withoutTrailingZeros) {
    if (withoutCurrency && !withoutTrailingZeros) {
      output = moneyObject.amount
    } else if (!withoutCurrency && withoutTrailingZeros) {
      output = moneyObject.withoutTrailingZeros
    } else {
      // both
      output = moneyObject.withoutTrailingZerosAndCurrency
    }
  }

  return (
    <Component {...passthroughProps}>
      {output}
      {measurement && measurement.referenceUnit && (
        <>
          {measurementSeparator}
          {measurement.referenceUnit}
        </>
      )}
    </Component>
  )
}

// required in order to narrow the money object down and make TS happy
function isMoney(
  maybeMoney: PartialDeep<MoneyV2, { recurseIntoArrays: true }>,
): maybeMoney is MoneyV2 {
  return (
    typeof maybeMoney.amount === 'string' &&
    !!maybeMoney.amount &&
    typeof maybeMoney.currencyCode === 'string' &&
    !!maybeMoney.currencyCode
  )
}
